"use client";

import React from "react";
import { Mail, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-12 rounded-md bg-primary/20 flex items-center justify-center text-primary mb-2 shadow-lg shadow-primary/20">
          <CircleCheck className="size-6" />
        </div>
        <h3 className="text-xl font-default font-bold text-foreground uppercase tracking-wider">¡BIENVENIDO A LA TRIBU!</h3>
        <p className="text-muted-foreground font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default uppercase tracking-widest text-xs text-primary">Nombre</FormLabel>
              <FormControl>
                <Input placeholder="TU NOMBRE" {...field} className="bg-transparent border-0 border-b-2 border-primary rounded-md focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-sans text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico no válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default uppercase tracking-widest text-xs text-primary">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="TU@EMAIL.COM" {...field} className="bg-transparent border-0 border-b-2 border-primary rounded-md focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-sans text-xs" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 rounded-md bg-destructive/20 border border-destructive/40 flex items-start gap-2 text-destructive text-sm font-sans">
          <CircleAlert className="size-4 mt-0.5 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary hover:bg-accent text-primary-foreground font-default font-bold text-lg tracking-widest rounded-md transition-all hover:shadow-lg hover:shadow-primary/40 uppercase px-8">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            ENVIANDO...
          </> : <>
            <Mail className="mr-2 size-4" />
            SEND IT
          </>}
      </Button>

      <p className="text-xs text-muted-foreground mt-4 text-center sm:text-left font-sans tracking-wide">
        AL UNIRTE, ACEPTAS NUESTRA{" "}
        <Link to="#" className="underline underline-offset-4 hover:text-primary transition-colors font-bold">
          POLÍTICA DE PRIVACIDAD
        </Link>
        .
      </p>
    </Form>;
}
export default function JoinTheTacoTribe() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="259322" id="join-the-taco-tribe" className="py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-md bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-md bg-accent/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="border-primary/30 shadow-2xl bg-card/80 backdrop-blur-md rounded-md overflow-hidden">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto size-14 rounded-md bg-primary/20 flex items-center justify-center text-primary mb-6 shadow-lg shadow-primary/10">
                <Mail className="size-7" />
              </div>
              <CardTitle className="text-5xl md:text-7xl font-default font-bold tracking-tighter text-foreground mb-4 uppercase">
                JOIN THE TACO TRIBE
              </CardTitle>
              <CardDescription className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto font-sans leading-relaxed">
                No te pierdas ni un solo bocado. Recibe nuestras ubicaciones semanales y ofertas secretas directamente en tu radar.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider formKey="join_taco_tribe_home" formId="join_taco_tribe_home" sectionName="join-the-taco-tribe" defaultValues={{
              name: "",
              email: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="SEND IT" successMessage="¡YA ERES PARTE DE LA TRIBU! PREPÁRATE PARA EL SABOR REAL.">
                <NewsletterFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}